import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import './IELTSResults.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Navbar from '../components/Navbar/Navbar';
import { Base_URL } from '../Client/apiURL';
import { getSectionColor } from '../components/IELTS/styles/theme';
import { formatIELTSBandScore } from '../components/IELTS/utils/scoreHelpers';
import { normalizeIeltsTestKind } from '../components/IELTS/mockData';
const MiniExercises = React.lazy(() => import('../features/ieltsMini/MiniExercises'));


const PageContainer = styled(Container, { shouldForwardProp: (prop) => prop !== 'embedded' })(({ theme, embedded }) => ({
  paddingTop: embedded ? 0 : '120px',
  paddingBottom: '2rem',
  minHeight: embedded ? 0 : '100vh',
  ...(embedded && { paddingLeft: 0, paddingRight: 0, maxWidth: 'none !important' }),
  [theme.breakpoints.down('md')]: {
    paddingTop: embedded ? 0 : '100px',
    paddingLeft: embedded ? 0 : '1rem',
    paddingRight: embedded ? 0 : '1rem',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginBottom: '24px',
  borderRadius: '12px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: '#F8F9FA',
  color: '#333',
  fontSize: '14px',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  [theme.breakpoints.down('sm')]: {
    padding: '10px 20px',
    fontSize: '14px',
  },
}));

const SummaryCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
  marginBottom: '24px',
}));

const IELTSResults = ({ topContent = null, embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const reviewNavigation = { state: { resultsReturnTo: location.pathname + location.search } };
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [groupDetailData, setGroupDetailData] = useState(null);
  const [loadingGroupDetail, setLoadingGroupDetail] = useState(false);
  const viewMode = ['sectional', 'full', 'mini'].includes(searchParams.get('resultType')) ? searchParams.get('resultType') : 'all';
  const requestedPage = Number(searchParams.get('resultsPage'));
  const itemsPerPage = 10;
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [miniSummary, setMiniSummary] = useState(null);
  const [resumeLoadingAttemptId, setResumeLoadingAttemptId] = useState(null);
  
  const groupId = searchParams.get('groupId');
  const filteredTests = (userData?.tests || []).filter((test) => {
    if (viewMode === 'sectional') return test.test_type_label === 'sectional' || (test.test_type === 'sectional' && !test.full_test_attempt_id);
    if (viewMode === 'full') return test.test_type === 'full' || test.full_test_attempt_id;
    return true;
  });
  const totalPages = Math.ceil(filteredTests.length / itemsPerPage);
  const page = Math.min(Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1, Math.max(1, totalPages));

  useEffect(() => {
    if (viewMode === 'mini' || loading || error || groupId || !searchParams.has('resultsPage')) return;
    if (searchParams.get('resultsPage') === String(page)) return;
    const next = new URLSearchParams(searchParams);
    if (page === 1) next.delete('resultsPage');
    else next.set('resultsPage', String(page));
    setSearchParams(next, { replace: true });
  }, [viewMode, loading, error, groupId, page, searchParams, setSearchParams]);

  useEffect(() => {
    if (viewMode === 'mini') return;
    if (groupId) {
      fetchGroupDetailData(groupId);
    } else {
      fetchUserTestsInfo();
    }
  }, [groupId, viewMode]);


  const fetchUserTestsInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/getUserTestsInfo`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.responseCode === 200) {
        setUserData(data.response);
      } else {
        throw new Error(data.message || 'Failed to fetch user tests info');
      }
    } catch (err) {
      console.error('Error fetching user tests info:', err);
      setError(err.message || 'Failed to load test results');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDetailData = async (groupId) => {
    try {
      setLoadingGroupDetail(true);
      setError(null);
      
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/getAllTestsByGroupId/${groupId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.responseCode === 200) {
        setGroupDetailData(data.response);
      } else {
        throw new Error(data.message || 'Failed to fetch group detail data');
      }
    } catch (err) {
      console.error('Error fetching group detail data:', err);
      setError(err.message || 'Failed to load group details');
    } finally {
      setLoadingGroupDetail(false);
      setLoading(false);
    }
  };

  const handleReturnToDashboard = () => {
    navigate('/ielts-dashboard');
  };

  const handleViewGroupDetail = (groupId) => {
    const next = new URLSearchParams(searchParams);
    next.set('groupId', groupId.toString());
    setSearchParams(next);
  };

  const handleBackToOverview = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('groupId');
    setSearchParams(next);
    setGroupDetailData(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusChip = (status, test) => {
    const statusColors = {
      'completed': 'success',
      'pending_evaluation': 'warning',
      'in_progress': 'info',
      'failed': 'error'
    };
    const isFull = test && (test.test_type === 'full' || test.full_test_attempt_id);
    let label = (status || 'completed').replace(/_/g, ' ').toUpperCase();
    let color = statusColors[status] || 'default';
    if (isFull && status === 'in_progress') {
      label = 'IN PROGRESS';
      color = 'info';
    } else if (isFull && test?.closed_early) {
      label = 'INCOMPLETE';
      color = 'warning';
    }

    return (
      <Chip 
        label={label}
        color={color}
        size="small"
      />
    );
  };

  const getGroupIdChip = (groupId) => {
    if (groupId) {
      return (
        <Chip 
          label={`Group ${groupId}`} 
          color="primary" 
          size="small"
          sx={{ backgroundColor: '#996CFE', color: 'white' }}
        />
      );
    }
    return (
      <Chip 
        label="Individual" 
        color="default" 
        size="small"
        variant="outlined"
      />
    );
  };

  const handleShowDetails = (groupId) => {
    navigate(`/ielts-test-details?type=group&groupId=${groupId}`, reviewNavigation);
  };



  // Add handler for individual test details
  const handleShowIndividualTestDetails = (attemptId) => {
    navigate(`/ielts-test-details?type=individual&attemptId=${attemptId}`, reviewNavigation);
  };

  // Add new handler for full test details
  const handleShowFullTestDetails = (test) => {
    const fullTestAttemptId = test.full_test_attempt_id;
    navigate(`/ielts-test-details?type=full&fullTestAttemptId=${fullTestAttemptId}`, reviewNavigation);
  };

  /** Navigate to the same destination as the former "View details" action. */
  const handleTestHistoryRowNavigate = (test) => {
    if (test.test_type === 'full' || test.full_test_attempt_id) {
      if (test.full_test_attempt_id == null) return;
      handleShowFullTestDetails(test);
    } else if (test.attempt_id != null) {
      handleShowIndividualTestDetails(test.attempt_id);
    }
  };

  const testHasResumeAction = (test) =>
    (test.test_type === 'full' || test.full_test_attempt_id) &&
    test.status === 'in_progress';

  const navigateToFullTestResumeFromPayload = (payload, fullTestAttemptId) => {
    if (!payload?.tests?.length) return;
    const sectionOrder = ['Listening', 'Reading', 'Writing', 'Speaking'];
    const pickCategoryRow = (categoryName) => {
      const rows = payload.tests.filter((t) => t.category_name === categoryName);
      if (rows.length === 0) return null;
      const completedRow = rows.find((t) => t.category_attempt_status === 'completed');
      if (completedRow) return completedRow;
      const pendingRow = rows.find((t) => t.category_attempt_status === 'pending_attempt');
      return pendingRow || rows[0];
    };
    const groupedTests = sectionOrder
      .map((categoryName) => pickCategoryRow(categoryName))
      .filter(Boolean)
      .map((t) => ({
        id: t.id,
        category_name: t.category_name,
        groupId: payload.groupId,
        group_name: t.group_name,
        test_type: t.test_type,
        audio_url: t.audio_url,
        sections: t.sections || [],
      }));
    navigate('/ielts-full-test', {
      state: {
        mode: 'practice',
        groupedTests,
        groupId: payload.groupId,
        continuePrevious: true,
        resumeFullTestAttemptId: Number(fullTestAttemptId),
        prefetchedResumeContext: {
          ...payload,
          fullTestAttemptId: Number(fullTestAttemptId),
        },
      },
    });
  };

  const handleResumeFullTest = async (test) => {
    const fullTestAttemptId = test.full_test_attempt_id || test.fullTestAttemptId;
    if (!fullTestAttemptId) return;
    const embedded = test.resumeContext;
    if (embedded?.tests?.length) {
      navigateToFullTestResumeFromPayload(embedded, fullTestAttemptId);
      return;
    }
    try {
      setResumeLoadingAttemptId(fullTestAttemptId);
      const response = await fetch(
        `${Base_URL}/app/users/ielts-tests/full-attempts/${fullTestAttemptId}/resume-context`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      const data = await response.json();
      const payload = data?.response;
      if (!response.ok || data?.responseCode !== 200 || !payload?.tests?.length) {
        throw new Error(data?.message || 'Unable to load full test for resume');
      }
      navigateToFullTestResumeFromPayload(payload, fullTestAttemptId);
    } catch (error) {
      console.error('Resume full test failed:', error);
      setError(error.message || 'Unable to resume full test');
    } finally {
      setResumeLoadingAttemptId(null);
    }
  };

  const handleOpenDeleteConfirm = (test) => {
    const isFull = test.test_type === 'full' || test.full_test_attempt_id;
    setDeleteConfirm({
      type: isFull ? 'full' : 'individual',
      id: isFull ? (test.full_test_attempt_id || test.fullTestAttemptId) : test.attempt_id,
      testName: test.test_name || (isFull ? 'Full Test' : (test.category_name || 'Sectional')),
    });
  };

  const handleCloseDeleteConfirm = () => {
    if (!deletingId) setDeleteConfirm(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    const { type, id } = deleteConfirm;
    setDeletingId(id);
    try {
      const url = type === 'full'
        ? `${Base_URL}/app/users/ielts-tests/result/full/${id}`
        : `${Base_URL}/app/users/ielts-tests/result/individual/${id}`;
      const res = await fetch(url, { method: 'DELETE', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.responseCode === 200) {
        setDeleteConfirm(null);
        await fetchUserTestsInfo();
      } else {
        setError(data.message || 'Failed to delete result');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete result');
    } finally {
      setDeletingId(null);
    }
  };

  if (viewMode !== 'mini' && loading) {
    return (
      <>
        {!embedded && <Navbar />}
        <PageContainer maxWidth="lg" embedded={embedded}>
          {topContent}
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <Box textAlign="center">
              <CircularProgress size={60} sx={{ color: '#996CFE', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Loading your test results...
              </Typography>
            </Box>
          </Box>
        </PageContainer>
      </>
    );
  }

  if (viewMode !== 'mini' && error) {
    return (
      <>
        {!embedded && <Navbar />}
        <PageContainer maxWidth="lg" embedded={embedded}>
          {topContent}
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={groupId ? () => fetchGroupDetailData(groupId) : fetchUserTestsInfo}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
          
          <Box textAlign="center">
            {groupId && (
              <ActionButton
                variant="outlined"
                onClick={handleBackToOverview}
                startIcon={<ArrowBackIcon />}
                sx={{ 
                  borderColor: '#996CFE',
                  color: '#996CFE',
                  '&:hover': { 
                    borderColor: '#7C4DFF',
                    backgroundColor: '#F5F0FF'
                  },
                  mr: 2
                }}
              >
                Back to Overview
              </ActionButton>
            )}
            <ActionButton
              variant="contained"
              onClick={handleReturnToDashboard}
              startIcon={<HomeIcon />}
              sx={{ 
                backgroundColor: '#996CFE',
                '&:hover': { backgroundColor: '#7C4DFF' }
              }}
            >
              Return to Dashboard
            </ActionButton>
          </Box>
        </PageContainer>
      </>
    );
  }

  // Render group detail view
  if (groupId && viewMode !== 'mini') {
    return (
      <>
        {!embedded && <Navbar />}
        <PageContainer maxWidth="lg" embedded={embedded}>
          {topContent}
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <IconButton onClick={handleBackToOverview} color="primary">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ color: '#996CFE', fontWeight: 600 }}>
              Complete IELTS Test {groupId} - Detailed Results
            </Typography>
          </Box>

          {loadingGroupDetail ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
              <Box textAlign="center">
                <CircularProgress size={60} sx={{ color: '#996CFE', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Loading test details...
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Individual Test Attempts
              </Typography>
              
              <StyledTableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Test ID</StyledTableCell>
                      <StyledTableCell>Section</StyledTableCell>
                      <StyledTableCell>Test Type</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                      <StyledTableCell>Start Time</StyledTableCell>
                      <StyledTableCell>End Time</StyledTableCell>
                      <StyledTableCell>Score</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupDetailData?.tests?.length > 0 ? (
                      groupDetailData.tests.map((test) => (
                        test.attempts_summary?.map((attempt) => (
                          <TableRow key={`${test.id}-${attempt.start_time}`} hover>
                            <TableCell sx={{ fontWeight: 500 }}>#{test.id}</TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {test.category_name}
                                </Typography>
                                <Chip 
                                  label={test.test_type} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '10px', height: '18px' }}
                                />
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {test.test_name}
                              </Typography>
                            </TableCell>
                            <TableCell>{test.test_kind ? normalizeIeltsTestKind(test.test_kind) : 'Standard'}</TableCell>
                            <TableCell>{getStatusChip(attempt.status)}</TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(attempt.start_time)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(attempt.end_time)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatIELTSBandScore(attempt.totalMarksObtained, '0')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )) || (
                          <TableRow key={test.id}>
                            <TableCell sx={{ fontWeight: 500 }}>#{test.id}</TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {test.category_name}
                                </Typography>
                                <Chip 
                                  label={test.test_type} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '10px', height: '18px' }}
                                />
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {test.test_name}
                              </Typography>
                            </TableCell>
                            <TableCell>{test.test_kind ? normalizeIeltsTestKind(test.test_kind) : 'Standard'}</TableCell>
                            <TableCell>
                              <Chip label="NOT ATTEMPTED" color="default" size="small" />
                            </TableCell>
                            <TableCell>-</TableCell>
                            <TableCell>-</TableCell>
                            <TableCell>-</TableCell>
                          </TableRow>
                        )
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No test attempts found for this group
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </StyledTableContainer>

              <Divider sx={{ my: 3 }} />

              <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                <ActionButton
                  variant="outlined"
                  onClick={handleBackToOverview}
                  startIcon={<ArrowBackIcon />}
                  sx={{ 
                    borderColor: '#996CFE',
                    color: '#996CFE',
                    '&:hover': { 
                      borderColor: '#7C4DFF',
                      backgroundColor: '#F5F0FF'
                    }
                  }}
                >
                  Back to Overview
                </ActionButton>
                
                <ActionButton
                  variant="contained"
                  onClick={handleReturnToDashboard}
                  startIcon={<HomeIcon />}
                  sx={{ 
                    backgroundColor: '#996CFE',
                    '&:hover': { backgroundColor: '#7C4DFF' }
                  }}
                >
                  Return to Dashboard
                </ActionButton>
              </Box>
            </>
          )}
        </PageContainer>
      </>
    );
  }

  return (
    <>
      {!embedded && <Navbar />}
      <PageContainer className="ielts-history" maxWidth="lg" embedded={embedded}>
        {topContent}
        <Typography className="ielts-history-title" variant="h4" component="h1" gutterBottom sx={{ color: '#29213c', fontWeight: 700, mb: 3 }}>
          Test Results Overview
        </Typography>

        {/* User Summary */}
        {!embedded && viewMode !== 'mini' && <SummaryCard sx={{ background: 'linear-gradient(135deg, #F8F2FF 0%, #E8DFFF 100%)', border: '1px solid rgba(153, 108, 254, 0.2)' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#5E35B1' }}>
              User Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" sx={{ color: '#6A1B9A', fontWeight: 500 }}>Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#4A148C' }}>{userData?.user?.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" sx={{ color: '#6A1B9A', fontWeight: 500 }}>Email</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#4A148C' }}>{userData?.user?.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" sx={{ color: '#6A1B9A', fontWeight: 500 }}>Subscription</Typography>
                <Chip 
                  label={userData?.user?.is_subscribed ? 'Subscribed' : 'Free'} 
                  color={userData?.user?.is_subscribed ? 'success' : 'default'}
                  size="small"
                  sx={{ 
                    fontWeight: 600,
                    backgroundColor: userData?.user?.is_subscribed ? '#E8F5E9' : '#F5F5F5',
                    color: userData?.user?.is_subscribed ? '#2E7D32' : '#616161'
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </SummaryCard>}

        <section className="ielts-history-summary" aria-label="Test summary">
          {(viewMode === 'mini' ? [
            { label: 'Total mini attempts', value: miniSummary?.total ?? '—', tone: 'purple', icon: <AssessmentOutlinedIcon /> },
            { label: 'Completed mini attempts', value: miniSummary?.completed ?? '—', tone: 'rose', icon: <AssignmentOutlinedIcon /> },
            { label: 'Unfinished mini attempts', value: miniSummary?.unfinished ?? '—', tone: 'teal', icon: <InsightsOutlinedIcon /> },
          ] : [
            { label: 'Total test attempts', value: userData?.summary?.totalTestAttempts || 0, tone: 'purple', icon: <AssessmentOutlinedIcon /> },
            { label: 'Unique tests', value: userData?.summary?.uniqueTestsAttempted || 0, tone: 'rose', icon: <AssignmentOutlinedIcon /> },
            { label: 'Average score', value: formatIELTSBandScore(userData?.summary?.averageScore, '0'), tone: 'teal', icon: <InsightsOutlinedIcon /> },
          ]).map((item) => (
            <div className={`ielts-history-stat ${item.tone}`} key={item.label}>
              <span className="ielts-history-stat-icon">{item.icon}</span>
              <div><span>{item.label}</span><strong>{item.value}</strong></div>
            </div>
          ))}
        </section>
        {viewMode !== 'mini' && <div className="ielts-history-breakdown">
          <span>Sectional attempts <strong>{userData?.summary?.sectionalTestAttempts || 0}</strong></span>
          <span>Full test attempts <strong>{userData?.summary?.fullTestAttempts || 0}</strong></span>
        </div>}

        {/* Tests Table */}
        <Box className="ielts-history-toolbar" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Test History
          </Typography>
          <Box className="ielts-history-top-pagination">
          {viewMode !== 'mini' && totalPages > 1 && (
            <Pagination
              aria-label="Top results pagination"
              count={totalPages}
              page={page}
              onChange={(event, newPage) => {
                const next = new URLSearchParams(searchParams);
                next.set('resultsPage', String(newPage));
                setSearchParams(next);
              }}
              color="primary"
              size="small"
              shape="rounded"
              siblingCount={0}
              boundaryCount={1}
            />
          )}
          </Box>
          <ToggleButtonGroup
            color="primary"
            value={viewMode}
            exclusive
            onChange={(e, newViewMode) => {
              if (!newViewMode) return;
              const next = new URLSearchParams(searchParams);
              next.set('resultType', newViewMode);
              next.delete('resultsPage');
              next.delete('groupId');
              setSearchParams(next);
            }}
            size="small"
            sx={{ 
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                border: '1px solid rgba(153, 108, 254, 0.5)',
                '&.Mui-selected': {
                  backgroundColor: '#996CFE',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#7C4DFF',
                  }
                }
              }
            }}
          >
            <ToggleButton value="all">All Tests</ToggleButton>
            <ToggleButton value="sectional">Sectional</ToggleButton>
            <ToggleButton value="full">Full Tests</ToggleButton>
            <ToggleButton value="mini">Mini Exercises</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        {viewMode === 'mini' ? <React.Suspense fallback={<CircularProgress aria-label="Loading mini results" />}><MiniExercises historyOnly onHistorySummary={setMiniSummary} /></React.Suspense> : <><StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>Test Name</StyledTableCell>
                <StyledTableCell>Date</StyledTableCell>
                <StyledTableCell>Type</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                if (filteredTests.length === 0) {
                  return (
                    <TableRow key="no-tests">
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No {viewMode === 'all' ? '' : viewMode} tests found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }

                // Pagination calculation
                const startIndex = (page - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const paginatedTests = filteredTests.slice(startIndex, endIndex);

                return paginatedTests.map((test, index) => {
                  const rowKey = test.full_test_attempt_id || test.fullTestAttemptId
                    ? `full-${test.full_test_attempt_id || test.fullTestAttemptId}`
                    : `sectional-${test.attempt_id || test.testId || test.testid || index}`;

                  return (
                   <TableRow
                     key={rowKey}
                     hover
                     onClick={() => handleTestHistoryRowNavigate(test)}
                     tabIndex={0}
                     onKeyDown={(e) => {
                       if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                         e.preventDefault();
                         handleTestHistoryRowNavigate(test);
                       }
                     }}
                     sx={{ cursor: 'pointer' }}
                   >
                     <TableCell sx={{ fontWeight: 500 }}>
                       <Typography variant="body1">{test.test_name}</Typography>
                       <Typography variant="caption" color="text.secondary">
                         {(test.test_type === 'full' || test.full_test_attempt_id) ? 'Full Test' : 'Sectional Test'}
                         {test.category_name && ` - ${test.category_name}`}
                       </Typography>
                     </TableCell>
                     <TableCell>
                       <Typography variant="body2">
                         {test.date_time ? new Date(test.date_time).toLocaleDateString() : 'N/A'}
                       </Typography>
                       <Typography variant="caption" color="text.secondary">
                         {test.date_time ? new Date(test.date_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                       </Typography>
                     </TableCell>
                     <TableCell>
                       {(test.test_type === 'full' || test.full_test_attempt_id) ? (
                         <Chip 
                           label="Full Test" 
                           color="primary" 
                           size="small" 
                           sx={{ minWidth: 80 }}
                         />
                      ) : (
                        (() => {
                          const sectionColor = getSectionColor(test.category_name);
                          return (
                            <Chip 
                              label={test.category_name || "Sectional"} 
                              size="small"
                              sx={{ 
                                minWidth: 80,
                                backgroundColor: sectionColor.primary,
                                color: 'white',
                                fontWeight: 600,
                                '&:hover': {
                                  backgroundColor: sectionColor.hover,
                                  color: 'white',
                                },
                              }}
                            />
                          );
                        })()
                      )}
                     </TableCell>
                     <TableCell>
                       {getStatusChip(test.status || 'completed', test)}
                     </TableCell>
                     <TableCell onClick={(e) => e.stopPropagation()}>
                       <Stack direction="row" spacing={1}>
                         <Button variant="outlined" startIcon={<VisibilityOutlinedIcon />} onClick={() => handleTestHistoryRowNavigate(test)}>View</Button>
                         {testHasResumeAction(test) && <Button variant="outlined"
                           disabled={resumeLoadingAttemptId === (test.full_test_attempt_id || test.fullTestAttemptId)}
                           startIcon={resumeLoadingAttemptId === (test.full_test_attempt_id || test.fullTestAttemptId) ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                           onClick={() => handleResumeFullTest(test)}>
                           {resumeLoadingAttemptId === (test.full_test_attempt_id || test.fullTestAttemptId) ? 'Resuming...' : 'Resume'}
                         </Button>}
                       </Stack>
                     </TableCell>
                   </TableRow>
                  );
                });
               })()}
               {!userData?.tests?.length && (
                 <TableRow>
                   <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                     <Typography variant="body1" color="text.secondary">
                       No tests found
                     </Typography>
                   </TableCell>
                 </TableRow>
               )}
             </TableBody>
           </Table>
         </StyledTableContainer>

        {/* Pagination */}
        {(() => {
          return totalPages > 1 ? (
            <Box display="flex" justifyContent="center" mt={3} mb={2}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={(e, newPage) => {
                  const next = new URLSearchParams(searchParams);
                  next.set('resultsPage', String(newPage));
                  setSearchParams(next);
                }}
                color="primary"
                size="medium"
                shape="rounded"
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#4A5568',
                    fontWeight: 700,
                    fontSize: '13px',
                    borderRadius: '8px',
                    fontFamily: "'Poppins', 'Inter', sans-serif",
                    '&.Mui-selected': {
                      backgroundColor: '#996CFE',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#7C4DFF',
                      },
                    },
                    '&:hover': {
                      backgroundColor: '#F7FAFC',
                    },
                  },
                  '& .MuiPaginationItem-ellipsis': {
                    color: '#A0AEC0',
                  },
                  '& .MuiPaginationItem-previousNext': {
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                  },
                }}
              />
            </Box>
          ) : null;
        })()}</>}

        <Dialog open={!!deleteConfirm} onClose={handleCloseDeleteConfirm}>
          <DialogTitle>Delete IELTS result?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {deleteConfirm && (
                <>
                  This will permanently delete the result for &quot;{deleteConfirm.testName}&quot;.
                  This action cannot be undone.
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteConfirm} disabled={!!deletingId}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={!!deletingId}
              startIcon={deletingId ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {deletingId ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

      </PageContainer>
    </>
  );
};

export default IELTSResults;
